export const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Diario' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensual' },
];

export const DAYS_OF_WEEK = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
];

export function defaultTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City';
  } catch {
    return 'America/Mexico_City';
  }
}

export function createScheduleSlot(overrides = {}) {
  return {
    id: overrides.id || `slot-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    frequency: overrides.frequency || 'daily',
    time: overrides.time || '09:00',
    dayOfWeek: overrides.dayOfWeek != null ? overrides.dayOfWeek : 1,
    dayOfMonth: overrides.dayOfMonth != null ? overrides.dayOfMonth : 1,
  };
}

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());
}

export function parseEmailList(raw) {
  if (!raw) return [];
  const list = Array.isArray(raw)
    ? raw
    : String(raw)
        .split(/[,;\n]+/)
        .map((s) => s.trim());
  return [...new Set(list.filter(isValidEmail))];
}

function normalizePdfMode(pdfMode) {
  return pdfMode === 'perDashboard' ? 'perDashboard' : 'combined';
}

export function normalizeReportSchedule(schedule) {
  if (!schedule || typeof schedule !== 'object') {
    return {
      enabled: false,
      timezone: defaultTimezone(),
      emailRecipients: [],
      pdfMode: 'combined',
      slots: [],
    };
  }

  const emailRecipients = parseEmailList(schedule.emailRecipients);
  const pdfMode = normalizePdfMode(schedule.pdfMode);

  if (Array.isArray(schedule.slots) && schedule.slots.length) {
    return {
      enabled: schedule.enabled === true,
      timezone: schedule.timezone || defaultTimezone(),
      emailRecipients,
      pdfMode,
      slots: schedule.slots
        .filter((s) => s && typeof s.time === 'string' && s.time.trim())
        .map((s) =>
          createScheduleSlot({
            id: s.id,
            frequency: s.frequency || 'daily',
            time: s.time.trim(),
            dayOfWeek: s.dayOfWeek,
            dayOfMonth: s.dayOfMonth,
          })
        ),
    };
  }

  if (schedule.time && schedule.frequency) {
    return {
      enabled: schedule.enabled === true,
      timezone: schedule.timezone || defaultTimezone(),
      emailRecipients,
      pdfMode,
      slots: [
        createScheduleSlot({
          id: 'legacy',
          frequency: schedule.frequency,
          time: schedule.time,
          dayOfWeek: schedule.dayOfWeek,
          dayOfMonth: schedule.dayOfMonth,
        }),
      ],
    };
  }

  return {
    enabled: schedule.enabled === true,
    timezone: schedule.timezone || defaultTimezone(),
    emailRecipients,
    pdfMode,
    slots: [],
  };
}

export function describeSlot(slot) {
  if (!slot) return '';
  const freq = FREQUENCY_OPTIONS.find((f) => f.value === slot.frequency)?.label || slot.frequency;
  let dayDesc = '';
  if (slot.frequency === 'weekly') {
    dayDesc = ` - ${DAYS_OF_WEEK.find((d) => d.value === slot.dayOfWeek)?.label || slot.dayOfWeek}`;
  } else if (slot.frequency === 'monthly') {
    dayDesc = ` - día ${slot.dayOfMonth}`;
  }
  return `${freq}${dayDesc} a las ${slot.time}`;
}

export function getScheduleDescription(report) {
  const norm = normalizeReportSchedule(report?.schedule);
  if (!norm.enabled || !norm.slots.length) return 'Sin programación';
  const tz = norm.timezone ? ` (${norm.timezone})` : '';
  const emailNote =
    norm.emailRecipients.length > 0
      ? ` · ${norm.emailRecipients.length} destinatario${norm.emailRecipients.length !== 1 ? 's' : ''}`
      : '';
  if (norm.slots.length === 1) return `${describeSlot(norm.slots[0])}${tz}${emailNote}`;
  return `${norm.slots.length} horarios${tz}: ${norm.slots.map(describeSlot).join('; ')}${emailNote}`;
}

export function buildSchedulePayload({ enabled, timezone, emailRecipientsText, slots, pdfMode }) {
  const emailRecipients = parseEmailList(emailRecipientsText);
  const normalizedPdfMode = normalizePdfMode(pdfMode);
  if (!enabled) {
    return { enabled: false, emailRecipients, pdfMode: normalizedPdfMode, slots: [] };
  }
  return {
    enabled: true,
    timezone: timezone || defaultTimezone(),
    emailRecipients,
    pdfMode: normalizedPdfMode,
    slots: (slots || []).map((s) => ({
      id: s.id,
      frequency: s.frequency,
      time: s.time,
      ...(s.frequency === 'weekly' ? { dayOfWeek: Number(s.dayOfWeek) } : {}),
      ...(s.frequency === 'monthly' ? { dayOfMonth: Number(s.dayOfMonth) } : {}),
    })),
  };
}

function parseTimeParts(timeStr) {
  const parts = String(timeStr || '').trim().split(':');
  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10) || 0;
  if (Number.isNaN(hour) || hour < 0 || hour > 23) return null;
  return { hour, minute };
}

const WEEKDAY_SHORT = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

function getZonedDateParts(date, timeZone) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });
  const parts = fmt.formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  let hour = parseInt(get('hour') || '0', 10);
  if (hour === 24) hour = 0;
  return {
    year: parseInt(get('year') || '0', 10),
    month: parseInt(get('month') || '0', 10),
    day: parseInt(get('day') || '0', 10),
    hour,
    minute: parseInt(get('minute') || '0', 10),
    weekday: WEEKDAY_SHORT[get('weekday')] ?? 0,
  };
}

function zonedLocalToDate({ year, month, day, hour, minute }, timeZone) {
  let utc = Date.UTC(year, month - 1, day, hour, minute);
  for (let attempt = 0; attempt < 24; attempt++) {
    const p = getZonedDateParts(new Date(utc), timeZone);
    if (
      p.year === year &&
      p.month === month &&
      p.day === day &&
      p.hour === hour &&
      p.minute === minute
    ) {
      return new Date(utc);
    }
    const targetMinutes = Date.UTC(year, month - 1, day, hour, minute) / 60000;
    const actualMinutes = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) / 60000;
    utc += (targetMinutes - actualMinutes) * 60000;
  }
  return new Date(utc);
}

function addDaysInTimezone(baseYmd, days, timeZone) {
  const anchor = zonedLocalToDate({ ...baseYmd, hour: 12, minute: 0 }, timeZone);
  const shifted = new Date(anchor.getTime() + days * 86400000);
  const p = getZonedDateParts(shifted, timeZone);
  return { year: p.year, month: p.month, day: p.day };
}

/** Próxima ejecución de un slot en la zona horaria indicada. */
export function getNextSlotRunAt(slot, timezone, now = new Date()) {
  if (!slot?.time) return null;
  const timeParts = parseTimeParts(slot.time);
  if (!timeParts) return null;

  const tz = timezone || defaultTimezone();
  const cursor = getZonedDateParts(now, tz);
  const baseYmd = { year: cursor.year, month: cursor.month, day: cursor.day };

  for (let offset = 0; offset <= 400; offset++) {
    const dayYmd = offset === 0 ? baseYmd : addDaysInTimezone(baseYmd, offset, tz);
    const noon = zonedLocalToDate({ ...dayYmd, hour: 12, minute: 0 }, tz);
    const parts = getZonedDateParts(noon, tz);

    if (slot.frequency === 'weekly' && parts.weekday !== Number(slot.dayOfWeek)) continue;
    if (slot.frequency === 'monthly' && parts.day !== Number(slot.dayOfMonth)) continue;

    const candidate = zonedLocalToDate(
      {
        year: parts.year,
        month: parts.month,
        day: parts.day,
        hour: timeParts.hour,
        minute: timeParts.minute,
      },
      tz
    );

    if (candidate > now) return candidate;
  }

  return null;
}

/** Texto relativo en español, ej. "en 1 día, 3 horas y 20 minutos". */
export function formatDurationUntil(ms) {
  if (ms == null || Number.isNaN(ms)) return '';
  if (ms <= 0) return 'en menos de 1 minuto';

  const totalMinutes = Math.max(1, Math.floor(ms / 60000));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  const chunks = [];
  if (days > 0) chunks.push(`${days} día${days !== 1 ? 's' : ''}`);
  if (hours > 0) chunks.push(`${hours} hora${hours !== 1 ? 's' : ''}`);
  if (minutes > 0 || chunks.length === 0) {
    chunks.push(`${minutes} minuto${minutes !== 1 ? 's' : ''}`);
  }

  if (chunks.length === 1) return `en ${chunks[0]}`;
  if (chunks.length === 2) return `en ${chunks[0]} y ${chunks[1]}`;
  return `en ${chunks.slice(0, -1).join(', ')} y ${chunks[chunks.length - 1]}`;
}

export function formatNextRunAbsolute(date, timeZone, locale = 'es') {
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return date.toLocaleString(locale);
  }
}

/** Tooltip completo para un slot programado. */
export function getNextSlotRunHint(slot, timezone, now = new Date()) {
  if (!slot?.time) {
    return 'Indica una hora válida para calcular la próxima ejecución.';
  }
  const tz = timezone || defaultTimezone();
  const next = getNextSlotRunAt(slot, tz, now);
  if (!next) {
    return 'No se pudo calcular la próxima ejecución con esta configuración.';
  }
  const relative = formatDurationUntil(next.getTime() - now.getTime());
  const absolute = formatNextRunAbsolute(next, tz);
  return `Próxima ejecución ${relative}\n${absolute} (${tz})`;
}

/** Próximas ejecuciones de todos los slots de un reporte programado. */
export function getReportNextScheduleRuns(report, now = new Date()) {
  const norm = normalizeReportSchedule(report?.schedule);
  if (!norm.enabled || !norm.slots.length) return null;

  const tz = norm.timezone || defaultTimezone();
  const runs = norm.slots
    .map((slot) => {
      const nextAt = getNextSlotRunAt(slot, tz, now);
      if (!nextAt) return null;
      return {
        slotDescription: describeSlot(slot),
        nextAt,
        relative: formatDurationUntil(nextAt.getTime() - now.getTime()),
        absolute: formatNextRunAbsolute(nextAt, tz),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.nextAt - b.nextAt);

  if (!runs.length) return null;

  return {
    reportId: report.id,
    reportName: report.name,
    timezone: tz,
    runs,
    nextAt: runs[0].nextAt,
  };
}

/** Lista de reportes programados con sus próximas ejecuciones, ordenada por la más cercana. */
export function getScheduledReportsNextRuns(reports, now = new Date(), filterReportId = null) {
  return (reports || [])
    .filter((report) => filterReportId == null || Number(report.id) === Number(filterReportId))
    .map((report) => getReportNextScheduleRuns(report, now))
    .filter(Boolean)
    .sort((a, b) => a.nextAt - b.nextAt);
}
